import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPlus,
  FaFilter,
  FaSearch,
  FaStore,
  FaList,
  FaClipboardList,
  FaHeart,
  FaRegHeart,
  FaBookmark,
  FaRegBookmark,
  FaShareAlt,
  FaStar,
  FaToggleOn,
  FaToggleOff,
  FaSearchDollar,
  FaPlusCircle,
  FaPencilAlt,
  FaComments,
  FaChevronLeft,
  FaChevronRight,
  FaCode,
  FaPalette,
  FaBullhorn,
  FaTag,
  FaCheck,
  FaArrowRight,
  FaCamera,
  FaMusic,
  FaVideo,
  FaChartLine,
  FaPaintBrush,
  FaLanguage,
  FaMicrophone,
  FaBook,
  FaCog,
  FaGlobe,
  FaGamepad,
  FaMobile,
  FaDesktop,
  FaRegObjectGroup,
  FaImage,
  FaDrawPolygon,
  FaPencilRuler,
  FaMailBulk,
  FaShieldAlt,
  FaDatabase,
  FaRobot,
  FaCloud,
} from 'react-icons/fa';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import '../styles/Marketplace.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  ServiceFormModal,
  ContactModal,
  MyServices,
  ServiceViewModal,
  ServiceCard,
} from './';
import MessagesDrawer from './MessagesDrawer';
import RequestFormModal from './RequestFormModal';
import ServiceRequests from './ServiceRequests';
import { Helmet } from 'react-helmet-async';
import defaultAvatar from '../assets/default-avatar.png'; // Make sure to add this image to assets
import { CATEGORIES } from '../constants/categories';

// Update the CATEGORY_ICONS mapping with more specific icons
const CATEGORY_ICONS = {
  web_development: FaCode, // Code icon for web development
  mobile_development: FaMobile, // Mobile icon for mobile development
  software_development: FaDesktop, // Desktop icon for software development
  game_development: FaGamepad, // Gamepad icon for game development
  blockchain: FaDatabase, // Database icon for blockchain
  cybersecurity: FaShieldAlt, // Shield icon for cybersecurity
  ai_ml: FaRobot, // Robot icon for AI/ML
  cloud_computing: FaCloud, // Cloud icon for cloud services

  // Design Categories
  ui_design: FaRegObjectGroup, // Layout icon for UI design
  ux_design: FaPalette, // Palette icon for UX design
  graphic_design: FaDrawPolygon, // Drawing icon for graphic design
  logo_design: FaPencilRuler, // Ruler icon for logo design
  illustration: FaImage, // Image icon for illustration

  // Marketing Categories
  digital_marketing: FaChartLine, // Chart icon for digital marketing
  seo_sem: FaSearchDollar, // Search with dollar for SEO/SEM
  email_marketing: FaMailBulk, // Email icon for email marketing
  content_marketing: FaBullhorn, // Bullhorn for content marketing

  // Writing Categories
  copywriting: FaPencilAlt, // Pencil icon for copywriting
  technical_writing: FaBook, // Book icon for technical writing

  // Other Categories
  translation: FaLanguage, // Language icon for translation
  other: FaGlobe, // Globe icon for other categories
};

function Marketplace({ initialView = 'marketplace' }) {
  const { currentUser } = useAuth();
  const [services, setServices] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showMessages, setShowMessages] = useState(false);
  const [activeView, setActiveView] = useState(initialView);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [lovedServices, setLovedServices] = useState({});
  const [savedServices, setSavedServices] = useState({});
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Number of listings per page
  const servicesGridRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [autoSlide, setAutoSlide] = useState(true);
  const autoSlideInterval = useRef(null);
  const [featuredServices, setFeaturedServices] = useState([]);

  // Initial empty service form state
  const emptyServiceForm = {
    title: '',
    description: '',
    price: '',
    pricingType: 'fixed',
    category: '',
    imageUrl: '',
    isActive: true,
    deliveryTime: '7d',
    tags: [],
    features: [''],
    requirements: '',
  };

  const [serviceForm, setServiceForm] = useState(emptyServiceForm);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Filter services first based on category and search
  const filteredServices = services.filter((service) => {
    const matchesCategory =
      filterCategory === 'all' || service.category === filterCategory;
    const matchesSearch =
      searchTerm === '' ||
      service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Then paginate the filtered services
  const currentServices = filteredServices.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  // Add these effects to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, searchTerm]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to services grid with offset for header
    if (servicesGridRef.current) {
      const headerOffset = 100;
      const elementPosition =
        servicesGridRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    fetchServices();
    fetchUserInteractions();
    // Check if we should show the new listing form
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'new-listing') {
      setShowServiceForm(true);
    }
  }, [filterCategory, currentUser, location]);

  useEffect(() => {
    // Start auto-sliding only if there are featured services
    if (autoSlide && featuredServices.length > 1) {
      autoSlideInterval.current = setInterval(() => {
        setCurrentSlide((current) =>
          current === featuredServices.length - 1 ? 0 : current + 1,
        );
      }, 5000);
    }

    return () => {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
      }
    };
  }, [autoSlide, featuredServices.length]);

  const fetchUserInteractions = async () => {
    if (!currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();

      setLovedServices(userData?.lovedServices || {});
      setSavedServices(userData?.savedServices || {});
    } catch (error) {
      console.error('Error fetching user interactions:', error);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const servicesRef = collection(db, 'services');
      const snapshot = await getDocs(servicesRef);

      // Get all users data first
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersMap = new Map(
        usersSnapshot.docs.map((doc) => [doc.id, doc.data()]),
      );

      // Create a Map to store unique services by ID
      const servicesMap = new Map();

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const userData = usersMap.get(data.userId) || {};

        const serviceData = {
          id: doc.id,
          documentId: doc.id,
          ...data,
          // Add user details
          sellerName: userData.displayName || userData.fullName || 'Anonymous',
          sellerAvatar: userData.photoURL || userData.profilePictureUrl,
          sellerEmail: userData.email,
        };
        servicesMap.set(doc.id, serviceData);
      });

      // Convert Map to array and filter
      const rawServices = Array.from(servicesMap.values());

      // Filter active services
      const activeServices = rawServices.filter(
        (service) => service.isActive !== false,
      );

      // Filter by category
      const categoryFiltered = activeServices.filter(
        (service) =>
          filterCategory === 'all' || service.category === filterCategory,
      );

      setServices(categoryFiltered);

      // Set featured services
      const featured = categoryFiltered.filter((service) => service.isFeatured);
      setFeaturedServices(featured);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to fetch services');
      setLoading(false);
    }
  };

  const handleServiceSubmit = async (formData) => {
    try {
      if (formData.documentId) {
        // Update existing service using the correct document ID
        const serviceRef = doc(db, 'services', formData.documentId);

        // Remove documentId before updating
        const { documentId, ...updateData } = formData;

        await updateDoc(serviceRef, {
          ...updateData,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Create new service
        await addDoc(collection(db, 'services'), {
          ...formData,
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setShowAddModal(false);
      fetchServices();
    } catch (error) {
      console.error('Error submitting service:', error);
      alert('Failed to update service. Please try again.');
    }
  };

  // Update view handler
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === 'requests') {
      navigate('/marketplace/requests');
    } else {
      navigate('/marketplace');
    }
  };

  const handleLoveService = async (serviceId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const newLoveStatus = !lovedServices[serviceId];

      // Update Firestore
      await updateDoc(userRef, {
        [`lovedServices.${serviceId}`]: newLoveStatus,
      });

      // Update local lovedServices state
      setLovedServices((prev) => ({
        ...prev,
        [serviceId]: newLoveStatus,
      }));

      // Get current love count from all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const loveCount = usersSnapshot.docs.reduce((count, doc) => {
        const userData = doc.data();
        return count + (userData.lovedServices?.[serviceId] === true ? 1 : 0);
      }, 0);

      // Update local services state with accurate count
      setServices((prevServices) =>
        prevServices.map((service) =>
          service.id === serviceId
            ? {
                ...service,
                loveCount,
              }
            : service,
        ),
      );
    } catch (error) {
      console.error('Error updating love status:', error);
    }
  };

  const handleSaveService = async (serviceId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const newSaveStatus = !savedServices[serviceId];

      await updateDoc(userRef, {
        [`savedServices.${serviceId}`]: newSaveStatus,
      });

      setSavedServices((prev) => ({
        ...prev,
        [serviceId]: newSaveStatus,
      }));
    } catch (error) {
      console.error('Error updating save status:', error);
    }
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      web_development: <FaCode />,
      design: <FaPalette />,
      marketing: <FaBullhorn />,
      // Add more mappings as needed
      default: <FaTag />,
    };
    return icons[category] || icons.default;
  };

  // Add this function to get top categories
  const getTopCategories = () => {
    // Count services in each category
    const categoryCounts = services.reduce((acc, service) => {
      if (service.category) {
        acc[service.category] = (acc[service.category] || 0) + 1;
      }
      return acc;
    }, {});

    // Sort and get top 6 categories
    return Object.entries(CATEGORIES)
      .map(([key, label]) => ({
        key,
        label,
        count: categoryCounts[key] || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  };

  const handleSliderMouseEnter = () => {
    setAutoSlide(false);
  };

  const handleSliderMouseLeave = () => {
    setAutoSlide(true);
  };

  // When setting up edit mode, pass the document ID
  const handleEditClick = (service) => {
    setSelectedService({
      ...service,
      documentId: service.id, // Ensure we pass the Firestore document ID
    });
    setShowAddModal(true);
  };

  // Update the category card rendering section
  const renderCategoryCards = () => {
    // Get top 6 categories based on service count
    const topCategories = Object.entries(CATEGORIES)
      .map(([key, label]) => {
        const count = services.filter(
          (service) => service.category === key,
        ).length;
        return { key, label, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Limit to top 6 categories

    return topCategories.map(({ key, label, count }) => {
      const IconComponent = CATEGORY_ICONS[key] || FaGlobe;

      return (
        <button
          key={`category-card-${key}`}
          className={`category-card ${filterCategory === key ? 'active' : ''}`}
          onClick={() => {
            setFilterCategory(filterCategory === key ? 'all' : key);
          }}
        >
          <div className="category-icon">
            <IconComponent />
          </div>
          <span className="category-name">{label}</span>
          <span className="category-count">
            {count} {count === 1 ? 'service' : 'services'}
          </span>
        </button>
      );
    });
  };

  return (
    <>
      <Helmet>
        <title>OECSL Marketplace</title>
        <meta
          name="description"
          content="Browse and list services on OECSL Marketplace"
        />
      </Helmet>
      <div className="marketplace-page">
        {/* Navigation and Filter Bar */}
        <div className="marketplace-nav">
          <div className="nav-actions">
            {/* Always visible buttons */}
            <button
              className={`btn ${activeView === 'marketplace' ? 'active' : ''}`}
              onClick={() => handleViewChange('marketplace')}
              aria-label="Marketplace"
            >
              <FaStore /> <span>Marketplace</span>
            </button>

            <button
              className={`btn ${activeView === 'requests' ? 'active' : ''}`}
              onClick={() => handleViewChange('requests')}
              aria-label="Requests"
            >
              <FaClipboardList /> <span>Requests</span>
            </button>

            {/* Login/Register prompt buttons for public users */}
            {!currentUser && (
              <>
                <button
                  className="btn"
                  onClick={() => navigate('/login')}
                  aria-label="List Service"
                >
                  <FaPlusCircle /> <span>List Service</span>
                </button>
                <button
                  className="btn"
                  onClick={() => navigate('/login')}
                  aria-label="Post Request"
                >
                  <FaPencilAlt /> <span>Post Request</span>
                </button>
                <button
                  className="btn"
                  onClick={() => navigate('/login')}
                  aria-label="Messages"
                >
                  <FaComments /> <span>Messages</span>
                </button>
              </>
            )}

            {/* Member buttons */}
            {currentUser && (
              <>
                <button
                  className={`btn ${
                    activeView === 'myServices' ? 'active' : ''
                  }`}
                  onClick={() => handleViewChange('myServices')}
                  aria-label="My Services"
                >
                  <FaList /> <span>My Services</span>
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setServiceForm(emptyServiceForm);
                    setShowAddModal(true);
                  }}
                  aria-label="List Service"
                >
                  <FaPlusCircle /> <span>List Service</span>
                </button>
                <button
                  className="btn"
                  onClick={() => setShowRequestForm(true)}
                  aria-label="Post Request"
                >
                  <FaPencilAlt /> <span>Post Request</span>
                </button>
                <button
                  className="btn"
                  onClick={() => setShowMessages(true)}
                  aria-label="Messages"
                >
                  <FaComments /> <span>Messages</span>
                </button>
              </>
            )}
          </div>

          <div className="filter-container">
            <select
              className="filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORIES).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="search-input"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="marketplace-content">
          {!currentUser && activeView === 'marketplace' && (
            <div className="marketplace-hero">
              <div className="hero-content">
                <h1>Your Gateway to Professional Services</h1>
                <p className="hero-tagline">
                  Connect with Sri Lanka's top professionals and businesses
                </p>
                <div className="hero-description">
                  <p>
                    Discover a curated marketplace of verified experts offering
                    web development, design, marketing, and more. Join our
                    thriving community to access:
                  </p>
                  <ul className="hero-benefits">
                    <li>
                      <FaCheck /> Vetted professional services
                    </li>
                    <li>
                      <FaCheck /> Secure payment protection
                    </li>
                    <li>
                      <FaCheck /> Direct communication with providers
                    </li>
                    <li>
                      <FaCheck /> Quality work guarantee
                    </li>
                  </ul>
                </div>
                <div className="hero-actions">
                  <Link to="/register" className="btn-cta primary">
                    Join Now <FaArrowRight />
                  </Link>
                  <Link to="/login" className="btn-cta secondary">
                    Sign In to Explore
                  </Link>
                </div>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">{services.length}+</span>
                  <span className="stat-label">Active Services</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">Secure</span>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : activeView === 'myServices' && currentUser ? (
            <MyServices />
          ) : activeView === 'requests' ? (
            <ServiceRequests />
          ) : (
            <>
              {activeView === 'marketplace' && (
                <>
                  <div className="featured-services-section">
                    {featuredServices.length > 0 &&
                      filterCategory === 'all' && (
                        <div
                          className="featured-slider-wrapper"
                          key="featured-slider"
                        >
                          <h2 className="section-title">Featured Services</h2>
                          <div
                            className="featured-slider"
                            onMouseEnter={handleSliderMouseEnter}
                            onMouseLeave={handleSliderMouseLeave}
                          >
                            <button
                              className="slider-btn prev"
                              onClick={() => {
                                setCurrentSlide((current) =>
                                  current === 0
                                    ? featuredServices.length - 1
                                    : current - 1,
                                );
                                setAutoSlide(false);
                              }}
                            >
                              <FaChevronLeft />
                            </button>

                            <div className="slider-container">
                              {featuredServices.map((service, index) => (
                                <div
                                  key={`featured-slide-${service.id}-${index}`}
                                  className={`slider-item ${
                                    index === currentSlide ? 'active' : ''
                                  }`}
                                  style={{
                                    transform: `translateX(${
                                      (index - currentSlide) * 100
                                    }%)`,
                                  }}
                                >
                                  <div className="featured-card">
                                    <div className="featured-image">
                                      <img
                                        src={
                                          service.imageUrl ||
                                          '/default-service.jpg'
                                        }
                                        alt={service.title}
                                      />
                                    </div>
                                    <div className="featured-content">
                                      <h3>{service.title}</h3>
                                      <p className="featured-provider">
                                        by {service.sellerName}
                                      </p>
                                      <p className="featured-description">
                                        {service.description?.substring(0, 120)}
                                        ...
                                      </p>
                                      <button
                                        className="btn-view-featured"
                                        onClick={() =>
                                          handleServiceClick(service)
                                        }
                                      >
                                        View Details
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <button
                              className="slider-btn next"
                              onClick={() => {
                                setCurrentSlide((current) =>
                                  current === featuredServices.length - 1
                                    ? 0
                                    : current + 1,
                                );
                                setAutoSlide(false);
                              }}
                            >
                              <FaChevronRight />
                            </button>

                            <div className="slider-dots">
                              {featuredServices.map((service, index) => (
                                <button
                                  key={`featured-dot-${service.id}-${index}`}
                                  className={`dot ${
                                    index === currentSlide ? 'active' : ''
                                  }`}
                                  onClick={() => {
                                    setCurrentSlide(index);
                                    setAutoSlide(false);
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                  <div className="category-filters">
                    <div className="category-cards">
                      {renderCategoryCards()}
                    </div>
                    {filterCategory !== 'all' && (
                      <button
                        className="clear-filter-btn"
                        onClick={() => setFilterCategory('all')}
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                  <div className="services-grid" ref={servicesGridRef}>
                    {filteredServices.length > 0 ? (
                      currentServices.map((service) => (
                        <ServiceCard
                          key={`service-grid-item-${service.id}`}
                          service={{
                            ...service,
                            displayName: service.sellerName,
                            profilePictureUrl: service.sellerAvatar,
                          }}
                          onContactClick={() => {
                            setSelectedService(service);
                            setShowContactModal(true);
                          }}
                          isLoved={lovedServices[service.id]}
                          onLove={() => handleLoveService(service.id)}
                          isSaved={savedServices[service.id]}
                          onSave={() => handleSaveService(service.id)}
                          onEdit={() => handleEditClick(service)}
                          onDelete={async () => {
                            if (
                              window.confirm(
                                'Are you sure you want to delete this service?',
                              )
                            ) {
                              await deleteDoc(doc(db, 'services', service.id));
                              fetchServices();
                            }
                          }}
                          onToggleActive={async () => {
                            await updateDoc(doc(db, 'services', service.id), {
                              isActive: !service.isActive,
                            });
                            fetchServices();
                          }}
                        />
                      ))
                    ) : (
                      <div className="no-services">
                        <p>No services found</p>
                        <small>
                          {filterCategory !== 'all' || searchTerm
                            ? 'Try adjusting your filters or search terms'
                            : 'Be the first to list a service!'}
                        </small>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* Show pagination if there are enough items, regardless of user status */}
          {filteredServices.length > itemsPerPage && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="page-btn first"
              >
                First
              </button>

              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn prev"
              >
                Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((num) => {
                    return (
                      num === 1 ||
                      num === totalPages ||
                      Math.abs(currentPage - num) <= 2
                    );
                  })
                  .map((number, index, array) => (
                    <React.Fragment key={number}>
                      {index > 0 && array[index - 1] !== number - 1 && (
                        <span className="page-ellipsis">...</span>
                      )}
                      <button
                        className={`page-btn number ${
                          currentPage === number ? 'active' : ''
                        }`}
                        onClick={() => handlePageChange(number)}
                      >
                        {number}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn next"
              >
                Next
              </button>

              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="page-btn last"
              >
                Last
              </button>
            </div>
          )}

          {/* Show pagination info for all users */}
          {filteredServices.length > 0 && (
            <div className="pagination-info">
              <p>
                Showing {indexOfFirstItem + 1} to{' '}
                {Math.min(indexOfLastItem, filteredServices.length)} of{' '}
                {filteredServices.length} listings
              </p>
            </div>
          )}
        </div>

        {/* Modals */}
        {showAddModal && (
          <ServiceFormModal
            service={serviceForm}
            onSubmit={handleServiceSubmit}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {showContactModal && selectedService && (
          <ContactModal
            service={selectedService}
            onSubmit={async (message) => {
              setShowContactModal(false);
              // Optionally show success message
              alert('Message sent successfully!');
            }}
            onClose={() => setShowContactModal(false)}
          />
        )}

        {showRequestForm && (
          <RequestFormModal
            onSubmit={async (formData) => {
              try {
                await addDoc(collection(db, 'buyerRequests'), {
                  ...formData,
                  userId: currentUser.uid,
                  createdAt: serverTimestamp(),
                  status: 'open',
                });
                setShowRequestForm(false);
                handleViewChange('requests'); // Navigate to requests view after submission
              } catch (error) {
                console.error('Error submitting request:', error);
                alert('Failed to submit request. Please try again.');
              }
            }}
            onClose={() => setShowRequestForm(false)}
          />
        )}

        {/* Messages Drawer */}
        <MessagesDrawer
          isOpen={showMessages}
          onClose={() => setShowMessages(false)}
        />

        {showServiceModal && selectedService && (
          <ServiceViewModal
            service={selectedService}
            onClose={() => setShowServiceModal(false)}
            onContact={() => {
              setShowServiceModal(false);
              setShowContactModal(true);
            }}
          />
        )}
      </div>
    </>
  );
}

Marketplace.propTypes = {
  initialView: PropTypes.oneOf(['marketplace', 'requests', 'myServices']),
};

export default Marketplace;
